import pluginContext from './utils/context'
import messageBus from '@mailonline/dmg-ads-core/src/ads-utils/MessageBus'
import { createLog } from '@mailonline/dmg-ads-core/src/ads-utils/log'
import { initialisePlugins } from '@mailonline/dmg-ads-core/src/plugin-manager'
import processQueue from '@mailonline/dmg-ads-core/src/processQueue'
import {init as initConfig, merge as mergeConfig, get as getConfig, set as setConfig} from '@mailonline/dmg-ads-core/src/config'
import { uniq } from '@mailonline/dmg-ads-core/src/ads-utils/ArrayUtils'
import { setupTcfv2 } from "@mailonline/dmg-ads-core/src/plugins/tcfv2/stub"
import pLocation from './utils/geo'
import device from './utils/device'

import * as plugins from './modules/essential'

const ESSENTIAL_PLUGINS = [
  "scriptLoader",
  "account",
  "slotController",
  "slotDefinition",
  "conditionalLoad",
  "refreshManager",
  "runtimeMessages",
  "exposeAdsData",
  "analytics.bidmax",
  "analytics.timeSpent",
  "analytics.partners",
  "analytics.pageMeta",
  "analytics.pageUnload",
  "analyticsBidmaxSendBeacon",
  "analyticsBidmaxSendBeaconMigration",
  "analyticsViewability",
  "analyticsStorage",
  "analyticsMemory",
  "analyticsRtaIds",
  "analyticsExternalData",
  "analyticsTiming",
  "analytics.impressions.banner",
  "analytics.coreMetrics",
  "analytics.reportPlugins",
  "revenueReporter",
  "refreshInfo",
  "userId",
  "trackers.watchdog",
  "skyConditionalSizes",
  "slotDescriptions",
  "sessionDepth",
  "debug.setPlugin",
  "adsDisabler",
  "visits",
  "scroll-observer"
]

const molAdsConfigDataP = (host, baseUrl, channel = 'home', subchannel = 'none', pageType = 'channel') => (new Promise(async (resolve, reject) => {
  const geo = (await pLocation)?.Country?.toLowerCase()

  // /v:version/:country/:channel/:subchannel/:pageType/:device/:articleId(\\d+)/:classicHomepage/:articleStyle/:host/config.:type(json|html)
  const endpoint = `https://${baseUrl}/v8/${geo}/${channel}/${subchannel}/${pageType}/${device}/false/${host}/config.json`

  fetch(endpoint)
    .then(response => resolve(response.json()))
}))

export default (async () => {
  const log = createLog('bootstrap')

  const PageCriteria = await pLocation

  global.PageCriteria = {
    ...(global.PageCriteria || {}),
    ...(PageCriteria || {}),
    region: PageCriteria.REGION_CODE // For mol-ads-config-data clientTargeting
  }

  /**
   * Init Config Data: local
   *
   * Query string params '?ac.exmaple=123'
   * Pre-defined global.adverts.config
   */

  initConfig()

  setConfig('site', getConfig('site', getConfig('mol-ads-config-data.host', global.location.hostname)))


  /**
   * Import Modules
   */

  const modules = getConfig('modules', [])

  await Promise.all(
    modules.map(module => import(`./modules/${module}`).catch(e => log.warn('Error importing module', e)))
  )


  /**
   * Create page context
   */

  const context = await pluginContext()
  log(context)


  /**
   * Init Config Data: mol-ads-config-data
   */

  const molAdsConfigDataHost = getConfig('mol-ads-config-data.host', 'an-ads')
  const molAdsConfigDataBaseUrl = getConfig('mol-ads-config-data.baseUrl', 'mads.dailymail.co.uk')

  if (molAdsConfigDataHost && molAdsConfigDataBaseUrl) {
    log('using mol-ads-config-data for host', molAdsConfigDataHost)

    const molAdsConfigData = await molAdsConfigDataP(
      molAdsConfigDataHost, 
      molAdsConfigDataBaseUrl,
      context.pageCriteria?.channel || 'home',
      context.pageCriteria?.subchannel || 'none',
      context.pageCriteria?.pageType || 'channel'
    )

    const { pluginsEnabled, config } = Object.entries(molAdsConfigData).reduce(
      ({ pluginsEnabled, config }, [k, v]) => {
        return k.startsWith('plugins.enabled')
          ? { pluginsEnabled: [...pluginsEnabled, [k, v]], config }
          : { pluginsEnabled, config: [...config, [k, v]] }
      },
      { pluginsEnabled: [], config: [] }
    )

    const _config = {
      ...Object.fromEntries(config),
      'plugins.enabled': [
        ...ESSENTIAL_PLUGINS,
        ...pluginsEnabled
        .sort((a, b) => a[0] - b[0])
        .map(([, pluginList]) => pluginList)
        .flat()
      ]
    }

    mergeConfig(_config)
    initConfig()
  }


  /**
   * Plugin Setup
   */

  const enabledPlugins = uniq([...ESSENTIAL_PLUGINS, ...getConfig('plugins.enabled', [])])

  setConfig('plugins.enabled', enabledPlugins)

  try {
    if (await context.isTCFV2Jurisdiction(getConfig)) {
      setupTcfv2();
    }

    await initialisePlugins({context, messageBus, plugins})
  } catch (e) {
    log.warn('Error initialising plugins', e)
  }

  /**
   * Process command queue
   */
  try {
    processQueue()
  } catch (e) {
    log.warn('Error initialising process queue', e)
  }
})()
